/**
 * Taking an organisation's audit trail elsewhere (docs/audit-export.md): an export a customer can
 * verify offline, and forwarding every row to the customer's SIEM as it is written.
 *
 *   GET /admin/v1/audit/export?from=&to=&format=ndjson|csv   the chain's rows in order with their hashes, then
 *                                                            one final line: AuditExportEnd (the chain's head and a count)
 *   GET  /admin/v1/audit/forwarding                          the destination (never its secret) and its standing
 *   PUT  /admin/v1/audit/forwarding                          set it: splunk-hec | sentinel | webhook | off
 *   POST /admin/v1/audit/forwarding/test                     send one test event now; the answer says how it went
 *
 * Forwarding walks the chain: every row after the last one delivered goes, oldest first, in batches
 * of up to AUDIT_FORWARD_BATCH, once a minute. A failure is retried with a growing wait.
 */
import { z } from "zod";
import { SENTINEL_STREAM } from "./agent365.js";
export const AUDIT_EXPORT_VERSION = 1;
/** The `type` of an export's final line, which is not a row. */
export const AUDIT_EXPORT_END = "aioe.audit.export.end";
/** Rows in one delivery to a SIEM. */
export const AUDIT_FORWARD_BATCH = 500;
export const auditExportFormatSchema = z.enum(["ndjson", "csv"]);
export const auditForwardKindSchema = z.enum(["splunk-hec", "sentinel", "webhook", "off"]);
const httpsUrl = z.string().trim().url().max(500);
const secret = z.string().min(1).max(4000);
/**
 * `PUT /admin/v1/audit/forwarding`. A secret left out keeps the one stored (so a change of index or
 * stream needs no secret again); a new destination (another kind or address) needs its secret.
 * `start`: where a new destination begins, `now` (rows written from now on, the default) or `beginning`
 * (the whole chain first). An unchanged destination carries on from where it was.
 */
export const auditForwardingPutSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("off") }).strict(),
    z
        .object({
        kind: z.literal("splunk-hec"),
        /** The HTTP Event Collector's address; with no path, /services/collector/event is added. */
        url: httpsUrl,
        /** The HEC token (sent as `Authorization: Splunk <token>`). */
        token: secret.optional(),
        index: z.string().trim().min(1).max(200).optional(),
        sourcetype: z.string().trim().min(1).max(200).optional(),
        start: z.enum(["now", "beginning"]).optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal("sentinel"),
        /** The data collection endpoint's (or rule's) logs ingestion address, https://<dce>.<region>-1.ingest.monitor.azure.com */
        endpoint: httpsUrl,
        /** The data collection rule's immutable id (dcr-…). */
        ruleId: z.string().trim().min(1).max(200),
        stream: z.string().trim().min(1).max(200).optional(),
        /** The customer's Microsoft Entra tenant (directory) id, where the application below is registered. */
        directoryId: z.string().trim().regex(/^[0-9a-fA-F-]{36}$|^[a-z0-9.-]+\.[a-z]{2,}$/i, "a directory (tenant) id or its domain").max(200),
        /** The application's client id; it holds Monitoring Metrics Publisher on the rule. */
        clientId: z.string().trim().min(1).max(200),
        clientSecret: secret.optional(),
        start: z.enum(["now", "beginning"]).optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal("webhook"),
        url: httpsUrl,
        /** What X-Aioe-Signature is the HMAC-SHA256 under: at least 16 characters. */
        secret: z.string().min(16).max(4000).optional(),
        start: z.enum(["now", "beginning"]).optional(),
    })
        .strict(),
]);
export const AUDIT_WEBHOOK_SIGNATURE_HEADER = "x-aioe-signature";
export const AUDIT_WEBHOOK_TIMESTAMP_HEADER = "x-aioe-timestamp";
export const AUDIT_FORWARD_DEFAULT_STREAM = SENTINEL_STREAM;
export const AUDIT_FORWARD_DEFAULT_SOURCETYPE = "aioe:audit";
//# sourceMappingURL=auditExport.js.map